import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package,
  FileText,
  Palette,
  Settings,
  ChevronRight,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  MapPin,
  RefreshCw,
  Download,
  Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const statusIcons = {
  pending: Clock,
  confirmed: CheckCircle,
  processing: Package,
  shipped: Truck,
  delivered: CheckCircle,
  cancelled: XCircle,
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [ordersRes, projectsRes] = await Promise.all([
          api.get('/api/orders?limit=5'),
          api.get('/api/custom-projects?limit=5'),
        ]);
        setOrders(ordersRes.data.slice(0, 5));
        setProjects(projectsRes.data.slice(0, 5));
      } catch {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, api, navigate]);

  if (!user) return null;

  const quickLinks = [
    { icon: Package, label: 'My Orders', href: '/dashboard/orders', color: 'bg-blue-50 text-blue-600' },
    { icon: Palette, label: 'My Projects', href: '/dashboard/projects', color: 'bg-purple-50 text-purple-600' },
    { icon: FileText, label: 'Request Quote', href: '/quote', color: 'bg-green-50 text-green-600' },
    { icon: Settings, label: 'Settings', href: '/dashboard/settings', color: 'bg-gray-50 text-gray-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="dashboard-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-navy">
            Welcome back, {user.name}!
          </h1>
          <p className="text-gray-600 mt-1">Manage your orders and projects</p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {quickLinks.map((link, index) => (
            <motion.div
              key={link.href}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={link.href}
                className="block bg-white rounded-2xl p-6 border border-gray-200 hover:border-mint/50 hover:shadow-lg transition-all card-hover"
              >
                <div className={`w-12 h-12 rounded-xl ${link.color} flex items-center justify-center mb-4`}>
                  <link.icon className="h-6 w-6" />
                </div>
                <span className="font-medium text-navy">{link.label}</span>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recent Orders */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-semibold text-navy">Recent Orders</h2>
              <Link to="/dashboard/orders" className="text-sm text-mint hover:underline flex items-center">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No orders yet</p>
                <Link to="/products">
                  <Button variant="link" className="text-mint mt-2">
                    Start shopping
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  const StatusIcon = statusIcons[order.status] || Clock;
                  return (
                    <Link
                      key={order.id}
                      to={`/dashboard/orders/${order.id}`}
                      className="block p-4 border border-gray-100 rounded-xl hover:border-mint/30 hover:bg-gray-50 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-navy">Order #{order.id.slice(0, 8)}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-navy">${order.total.toFixed(2)}</span>
                          <Badge className={statusColors[order.status]}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Recent Projects */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-semibold text-navy">Custom Projects</h2>
              <Link to="/dashboard/projects" className="text-sm text-mint hover:underline flex items-center">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner"></div>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8">
                <Palette className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No projects yet</p>
                <Link to="/custom-design">
                  <Button variant="link" className="text-mint mt-2">
                    Start a project
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {projects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/dashboard/projects/${project.id}`}
                    className="block p-4 border border-gray-100 rounded-xl hover:border-mint/30 hover:bg-gray-50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-navy">{project.title}</p>
                        <p className="text-sm text-gray-500">{project.project_type}</p>
                      </div>
                      <Badge className={statusColors[project.status] || 'bg-gray-100 text-gray-800'}>
                        {project.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Leave a Review */}
        <ReviewSection api={api} />
      </div>
    </div>
  );
}

function ReviewSection({ api }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0 || !title || !message) {
      toast.error('Please fill in all fields and select a rating');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/reviews', { rating, title, message });
      toast.success('Thank you! Your review will appear after approval.');
      setSubmitted(true);
    } catch {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-8 bg-white rounded-2xl p-6 border border-gray-200 text-center"
      >
        <CheckCircle className="h-10 w-10 text-mint mx-auto mb-3" />
        <h3 className="text-lg font-heading font-semibold text-navy">Review Submitted!</h3>
        <p className="text-gray-500 text-sm mt-1">It will appear on our homepage after approval. Thank you!</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="mt-8 bg-white rounded-2xl p-6 border border-gray-200"
      data-testid="review-section"
    >
      <h2 className="text-xl font-heading font-semibold text-navy mb-1">Leave a Review</h2>
      <p className="text-gray-500 text-sm mb-5">Share your experience with Printed LB</p>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-navy block mb-2">Rating</label>
          <div className="flex gap-1">
            {[1,2,3,4,5].map(s => (
              <button
                key={s}
                type="button"
                onMouseEnter={() => setHoverRating(s)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(s)}
                className="p-0.5"
                data-testid={`review-star-${s}`}
              >
                <Star className={`h-7 w-7 transition-colors ${
                  s <= (hoverRating || rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'
                }`} />
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-navy block mb-1.5">Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Sum it up in a few words"
            data-testid="review-title"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-navy block mb-1.5">Your Review</label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            placeholder="Tell us about your experience..."
            data-testid="review-message"
          />
        </div>
        <Button
          className="bg-mint hover:bg-mint-dark text-black rounded-full"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="review-submit-btn"
        >
          {submitting ? 'Submitting...' : 'Submit Review'}
        </Button>
      </div>
    </motion.div>
  );
}

export function OrdersPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        const { data } = await api.get('/api/orders');
        setOrders(data);
      } catch {
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user, api, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-heading font-bold text-navy">My Orders</h1>
          <Link to="/products">
            <Button className="bg-mint hover:bg-mint-dark text-black rounded-full">
              Shop Now
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
            <Package className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-heading font-semibold text-navy mb-2">No orders yet</h2>
            <p className="text-gray-500 mb-6">Start shopping to see your orders here</p>
            <Link to="/products">
              <Button className="bg-mint hover:bg-mint-dark text-black rounded-full">
                Browse Products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => {
              const StatusIcon = statusIcons[order.status] || Clock;
              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/dashboard/orders/${order.id}`}
                    className="block bg-white rounded-2xl p-6 border border-gray-200 hover:border-mint/30 hover:shadow-lg transition-all"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-heading font-semibold text-navy">
                            Order #{order.id.slice(0, 8)}
                          </p>
                          <Badge className={statusColors[order.status]}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {order.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {order.items?.length || 0} item(s)
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-navy">${order.total.toFixed(2)}</p>
                        <p className="text-sm text-gray-500 capitalize">{order.payment_method}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchOrder = async () => {
      try {
        const { data } = await api.get(`/api/orders/${id}`);
        setOrder(data);
      } catch {
        toast.error("Order not found");
        navigate('/dashboard/orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, user, api, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!order) return null;

  const StatusIcon = statusIcons[order.status] || Clock;

  const steps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
  const currentStepIndex = steps.indexOf(order.status);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-navy">
              Order #{order.id.slice(0, 8)}
            </h1>
            <p className="text-gray-500 mt-1">
              Placed on {new Date(order.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/invoice`, '_blank')}
              data-testid="download-invoice-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Invoice
            </Button>
            <Button
              variant="outline"
              className="border-mint text-mint hover:bg-mint/10 rounded-full"
              onClick={async () => {
                try {
                  await api.post('/api/orders/reorder', { order_id: order.id });
                  toast.success('Items added to cart!');
                  navigate('/cart');
                } catch {
                  toast.error('Failed to reorder');
                }
              }}
              data-testid="reorder-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reorder
            </Button>
            <Badge className={`${statusColors[order.status]} text-base px-4 py-2`}>
              <StatusIcon className="h-4 w-4 mr-2" />
              {order.status}
            </Badge>
          </div>
        </div>

        {/* Progress */}
        {order.status !== 'cancelled' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-gray-200 mb-8"
          >
            <h2 className="font-heading font-semibold text-navy mb-6">Order Progress</h2>
            <div className="relative">
              <div className="flex justify-between">
                {steps.map((step, index) => {
                  const isActive = index <= currentStepIndex;
                  const StepIcon = statusIcons[step];
                  return (
                    <div key={step} className="flex flex-col items-center relative z-10">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-mint text-white' : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        <StepIcon className="h-5 w-5" />
                      </div>
                      <span className={`text-xs mt-2 capitalize ${isActive ? 'text-navy font-medium' : 'text-gray-400'}`}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Progress Line */}
              <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-100">
                <div
                  className="h-full bg-mint transition-all duration-500"
                  style={{ width: `${(currentStepIndex / (steps.length - 1)) * 100}%` }}
                />
              </div>
            </div>

            {order.tracking_number && (
              <div className="mt-6 p-4 bg-mint/5 rounded-xl">
                <p className="text-sm text-gray-600">Tracking Number</p>
                <p className="font-mono font-semibold text-navy">{order.tracking_number}</p>
              </div>
            )}

            {/* Tracking Timeline */}
            {order.tracking_history && order.tracking_history.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-navy mb-4">Tracking Timeline</h3>
                <div className="space-y-4">
                  {order.tracking_history.map((entry, entryIdx) => (
                    <div key={entry.timestamp} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 bg-mint rounded-full border-2 border-white shadow" />
                        {entryIdx < order.tracking_history.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                      </div>
                      <div className="pb-4">
                        <p className="font-medium text-navy text-sm">{entry.label}</p>
                        {entry.note && <p className="text-xs text-gray-500 mt-0.5">{entry.note}</p>}
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Order Items */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-200"
          >
            <h2 className="font-heading font-semibold text-navy mb-6">Order Items</h2>
            <div className="space-y-4">
              {order.items?.map((item, index) => (
                <div key={index} className="flex gap-4 p-4 border border-gray-100 rounded-xl">
                  <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={item.product?.image_url || '/placeholder.png'}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-navy">{item.product?.name || 'Product'}</p>
                    <p className="text-sm text-gray-500">
                      {item.options?.size && `Size: ${item.options.size}`}
                      {item.options?.paper_type && ` • ${item.options.paper_type}`}
                    </p>
                    <div className="flex justify-between mt-2">
                      <span className="text-sm text-gray-500">Qty: {item.quantity}</span>
                      <span className="font-semibold text-navy">
                        ${(item.unit_price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Shipping Address */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h3 className="font-heading font-semibold text-navy mb-4 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-mint" />
                Shipping Address
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {order.shipping_address?.street}<br />
                {order.shipping_address?.city}, {order.shipping_address?.state}<br />
                {order.shipping_address?.postal_code}<br />
                {order.shipping_address?.country}
              </p>
            </div>

            {/* Payment Summary */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h3 className="font-heading font-semibold text-navy mb-4">Payment Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>${order.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span>{order.shipping === 0 ? 'Free' : `$${order.shipping?.toFixed(2)}`}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm text-mint font-medium">
                    <span>Discount {order.coupon_code && `(${order.coupon_code})`}</span>
                    <span>-${order.discount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold text-navy">Total</span>
                  <span className="font-bold text-xl text-navy">${order.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2">
                  <span className="text-gray-600">Payment Method</span>
                  <span className="capitalize">{order.payment_method}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Payment Status</span>
                  <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {order.payment_status}
                  </Badge>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchProjects = async () => {
      try {
        const { data } = await api.get('/api/custom-projects');
        setProjects(data);
      } catch {
        toast.error("Failed to load projects");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [user, api, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-heading font-bold text-navy">My Projects</h1>
          <Link to="/custom-design">
            <Button className="bg-mint hover:bg-mint-dark text-black rounded-full">
              New Project
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
            <Palette className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-heading font-semibold text-navy mb-2">No projects yet</h2>
            <p className="text-gray-500 mb-6">Start a custom design project to see it here</p>
            <Link to="/custom-design">
              <Button className="bg-mint hover:bg-mint-dark text-black rounded-full">
                Start a Project
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/dashboard/projects/${project.id}`}
                  className="block bg-white rounded-2xl p-6 border border-gray-200 hover:border-mint/30 hover:shadow-lg transition-all h-full"
                >
                  <div className="flex items-start justify-between mb-4">
                    <Badge className={statusColors[project.status] || 'bg-gray-100 text-gray-800'}>
                      {project.status}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-navy mb-2">{project.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">{project.project_type}</p>
                  <p className="text-sm text-gray-600 line-clamp-3">{project.description}</p>
                  {project.quoted_price && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <span className="text-sm text-gray-500">Quoted Price:</span>
                      <span className="font-bold text-navy ml-2">${project.quoted_price}</span>
                    </div>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
